import { ChangeEvent, ReactNode, SyntheticEvent, useEffect, useState } from 'react';
import { Folder, Image, Trash } from 'react-feather';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMutation, useServerErrors } from '../../../Shared/api';
import { Editor } from '../../../Shared/Editor/Editor';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { donationFields, PaymentMethod, Project } from '../../../Shared/types';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import { DateInput } from '../../../Shared/UI/DateInput';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../../Shared/UI/FadeIn';
import { FileInput } from '../../../Shared/UI/FileInput';
import { Icon } from '../../../Shared/UI/Icon';
import InfoMessage from '../../../Shared/UI/InfoMessage';
import { Input } from '../../../Shared/UI/Input';
import InputDescription from '../../../Shared/UI/InputDescription';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { InputWrapper } from '../../../Shared/UI/InputWrapper';
import Picker from '../../../Shared/UI/Picker';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { TimeInput } from '../../../Shared/UI/TimeInput';
import { useCurrencyInput } from '../../../Shared/useCurrencyInput';
import { useIntegerInput } from '../../../Shared/useIntegerInput';
import { useNumberInput } from '../../../Shared/useNumberInput';
import { useRouter } from '../../router';
import { Product } from '../../types';
import Layout from '../../UI/Layout';

export interface EditProductFormProps {
  product: Product & {
    projects: Project[];
  };
  projects: Project[];
  paymentMethods: PaymentMethod[];
}

export default function EditProductForm({
  product,
  projects,
  paymentMethods,
}: EditProductFormProps) {
  const { t } = useTranslation();
  const { handleCurrencyBlur, parseCurrency, formatCurrency: formatCurrencyInput } = useCurrencyInput();
  const { handleNumberBlur, formatNumber } = useNumberInput();
  const { handleIntegerBlur, formatInteger, parseInteger } = useIntegerInput();
  const { formatCurrency, formatDate, toIsoDateTimeString } = useLocale();
  const { routes } = useRouter();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      title: product.title,
      public_description: product.public_description,
      confirmation_mail_description: product.confirmation_mail_description,
      image: null,
      delete_image: false,
      terms_url: product.terms_url,
      campaigner_description: product.campaigner_description,
      require_opt_in: product.require_opt_in ? 'yes' : 'no',
      selling_price: formatCurrencyInput(product.selling_price),
      cost_price: formatCurrencyInput(product.cost_price),
      vat_percentage: product.vat_percentage ? formatNumber(product.vat_percentage) : null,
      required: product.required,
      hide_on_invoice: product.hide_on_invoice,
      payment_methods: product.payment_methods,
      required_fields: product.required_fields,
      start_date: product.start_at ? formatDate(product.start_at, 'internal_date') : null,
      start_time: product.start_at ? formatDate(product.start_at, 'internal_time') : null,
      end_date: product.end_at ? formatDate(product.end_at, 'internal_date') : null,
      end_time: product.end_at ? formatDate(product.end_at, 'internal_time') : null,
      capacity: product.capacity !== null ? formatInteger(product.capacity) : '',
      projects: [...product.projects.map((project) => ({ value: project.id })), { value: '' }],
    },
  });

  const {
    register, control, handleSubmit, setValue, watch, resetField, setError,
    formState: { defaultValues, errors },
  } = form;

  const currentImageUrl = product.image_url;
  const [image, setImage] = useState<File>();
  const deleteImage = watch('delete_image');
  const canDeleteImage = !deleteImage && (!!image || !!product.image_url);

  const handleImageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setImage(event.target.files?.[0] || undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', false, { shouldDirty: true });
    setImageSrc(undefined);
  };

  const unsetImage = () => {
    setImage(undefined);
    resetField('image'); // Reset errors
    setValue('delete_image', true, { shouldDirty: true });
    setImageSrc(undefined);
  };

  const [imageSrc, setImageSrc] = useState<string>();

  useEffect(() => {
    if (image) {
      const reader = new FileReader();
      reader.addEventListener('load', () => setImageSrc(reader.result?.toString() || undefined));
      reader.readAsDataURL(image);
    }
  }, [image]);

  const validateImage = (event: SyntheticEvent<HTMLImageElement>) => {
    const element = event.currentTarget;

    if (element.naturalWidth < 300) {
      unsetImage();
      setError('image', { message: t('shared:validation.image_too_small') });
    }
  };

  const startDate = watch('start_date');
  const endDate = watch('end_date');

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'projects',
  });

  const projectsValue = watch('projects');

  const selectedProjects = projectsValue.map((item) => (
    projects.filter((project) => project.id === item.value)[0]
  )).filter((project) => project);

  const selectedProjectIds = selectedProjects.map((project) => project.id);

  const handleProjectChange = () => {
    if (projectsValue[projectsValue.length - 1].value) {
      append({ value: '' });
    }
  };

  const requireOptIn = watch('require_opt_in') === 'yes';

  const sellingPrice = parseCurrency(watch('selling_price')) || 0;
  const costPrice = parseCurrency(watch('cost_price')) || 0;
  const selectedPaymentMethods = watch('payment_methods');

  const [editProduct, { errors: serverErrors, loading }] = useMutation(
    routes.edit_product(product.id),
    {
      onSuccess: () => {
        notifier.notify(t('backend:product_saved'));
      },
    }
  );

  useServerErrors(form, serverErrors);

  const submit = async (data: typeof defaultValues) => {
    await editProduct({
      title: data?.title,
      public_description: data?.public_description,
      confirmation_mail_description: data?.confirmation_mail_description,
      image,
      delete_image: data?.delete_image,
      terms_url: data?.terms_url,
      required_fields: data?.required_fields,
      campaigner_description: data?.campaigner_description,
      selling_price: data?.selling_price ? parseCurrency(data.selling_price) : null,
      cost_price: data?.cost_price ? parseCurrency(data.cost_price) : null,
      vat_percentage: data?.vat_percentage?.replace(',', '.'),
      required: data?.required,
      hide_on_invoice: data?.hide_on_invoice,
      payment_methods: data?.payment_methods,
      start_at: data?.start_date && toIsoDateTimeString(data?.start_date, data?.start_time || '00:00', 'internal_date_time'),
      end_at: data?.end_date ? toIsoDateTimeString(data?.end_date, data?.end_time || '00:00', 'internal_date_time') : null,
      projects: data?.projects?.map((field) => field?.value).filter((value) => !!value),
      capacity: data?.capacity ? parseInteger(data.capacity) : null,
      require_opt_in: data?.require_opt_in === 'yes',
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_product')}
          </h3>
        </div>
        {serverErrors && (
          <div>
            <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />
          </div>
        )}
        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:general')}
            </h4>
            <div className="mb-3 text-slate-500">
              {t('backend:product_general_description')}
            </div>
            <div className="text-slate-500">
              {t('backend:product_general_tip')}
            </div>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.title} htmlFor="edit_product_title">
                {t('backend:product_title')}
              </InputLabel>
              <Input
                {...register('title', { required: true })}
                id="edit_product_title"
                aria-invalid={!!errors.title}
              />
              <ErrorMessage error={errors.title} attribute={t('backend:product_title')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_product_image">
                {t('backend:image')}
              </InputLabel>
              <InputDescription>
                {t('backend:product_image_description')}
              </InputDescription>
              <div className="p-4 border rounded-md border-slate-250">
                <div
                  className="flex items-center bg-slate-50 rounded-sm justify-center transition-all w-[150px] aspect-square mb-4"
                >
                  {!image && !deleteImage && currentImageUrl && (
                    <img
                      src={currentImageUrl}
                      alt={product.title}
                      className="w-full h-full object-cover rounded-sm"
                    />
                  )}
                  {image && imageSrc && (
                    <img
                      src={imageSrc}
                      alt={product.title}
                      className="w-full h-full object-cover rounded-sm"
                      onLoad={validateImage}
                    />
                  )}
                  {((!image && !currentImageUrl) || deleteImage) && (
                    <Icon className="!w-12 !h-12 text-slate-250">
                      <Image strokeWidth={1} />
                    </Icon>
                  )}
                </div>
                <div className="flex space-x-2">
                  <FileInput
                    {...register('image')}
                    accept=".jpg,.jpeg,.png"
                    id="edit_product_image"
                    aria-invalid={!!errors.image}
                    onChange={handleImageChange}
                  >
                    <Icon className="mr-1">
                      <Image />
                    </Icon>
                    {t('shared:form.select')}
                  </FileInput>
                  {canDeleteImage && (
                    <Button onClick={() => unsetImage()} variant="tertiary" size="sm">
                      <Icon className="mr-1">
                        <Trash />
                      </Icon>
                      {t('shared:form.delete')}
                    </Button>
                  )}
                </div>
              </div>
              <ErrorMessage error={errors.image} attribute={t('backend:image')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_product_public_description">
                {t('backend:product_description')}
              </InputLabel>
              <InputDescription>
                {t('backend:product_description_description')}
              </InputDescription>
              <Controller
                control={control}
                name="public_description"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Editor
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <ErrorMessage error={errors.public_description} attribute={t('backend:product_description')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_product_confirmation_mail_description">
                {t('backend:product_confirmation_mail')}
              </InputLabel>
              <InputDescription>
                {t('backend:product_confirmation_mail_description')}
              </InputDescription>
              <Controller
                control={control}
                name="confirmation_mail_description"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Editor
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <ErrorMessage error={errors.confirmation_mail_description} attribute={t('backend:product_confirmation_mail')} />
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="edit_product_terms">
                {t('backend:terms_and_conditions')}
              </InputLabel>
              <InputDescription>
                {t('backend:product_terms_and_conditions_description')}
              </InputDescription>
              <Input
                {...register('terms_url')}
                id="edit_product_terms"
                aria-invalid={!!errors.terms_url}
                placeholder="https://"
              />
              <ErrorMessage error={errors.terms_url} attribute={t('backend:terms_and_conditions')} />
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.required_fields}>
                {t('backend:required_fields')}
              </InputLabel>
              {donationFields.map((donationField) => (
                <CheckboxLabel htmlFor={`donation_field_${donationField}`} key={donationField}>
                  <Checkbox
                    {...register('required_fields')}
                    value={donationField}
                    id={`donation_field_${donationField}`}
                  />
                  <span>
                    {t(`backend:donation_fields.${donationField}`)}
                  </span>
                </CheckboxLabel>
              ))}
            </InputGroup>
          </div>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:pricing')}
            </h4>
            <div className="text-slate-500">
              {t('backend:product_pricing_description')}
            </div>
          </div>
          <div className="space-y-6">
            <InputGroup>
              <InputLabel required valid={!errors.selling_price} htmlFor="edit_product_selling_price">
                {t('backend:selling_price')}
              </InputLabel>
              <InputDescription>
                {t('backend:product_selling_price_description')}
              </InputDescription>
              <div className="grid grid-cols-2 gap-6">
                <InputWrapper>
                  <label htmlFor="edit_product_selling_price">
                    €
                  </label>
                  <Input
                    {...register('selling_price', { required: true })}
                    id="edit_product_selling_price"
                    className="pl-7"
                    onBlur={(event) => {
                      handleCurrencyBlur(event);
                      setValue('selling_price', event.target.value);
                    }}
                    aria-invalid={!!errors.selling_price}
                    inputMode="decimal"
                    min={0}
                    max={99999999}
                  />
                </InputWrapper>
              </div>
              <ErrorMessage error={errors.selling_price} attribute={t('backend:selling_price')} />
            </InputGroup>
            <InputGroup>
              <InputLabel valid={!errors.cost_price} htmlFor="edit_product_cost_price">
                {t('backend:cost_price')}
              </InputLabel>
              <InputDescription>
                {t('backend:product_cost_price_description')}
              </InputDescription>
              <div className="grid grid-cols-2 gap-6">
                <InputWrapper>
                  <label htmlFor="edit_product_cost_price">
                  €
                  </label>
                  <Input
                    {...register('cost_price')}
                    id="edit_product_cost_price"
                    className="pl-7"
                    onBlur={(event) => {
                      handleCurrencyBlur(event);
                      setValue('cost_price', event.target.value);
                    }}
                    aria-invalid={!!errors.cost_price}
                    inputMode="decimal"
                    min={0}
                    max={sellingPrice}
                  />
                </InputWrapper>
              </div>
              <ErrorMessage error={errors.cost_price} attribute={t('backend:cost_price')} />
            </InputGroup>
            <InfoMessage>
              {t('backend:product_cost_price_calculation', {
                donationAmount: formatCurrency(Math.max(sellingPrice - costPrice, 0)),
              })}
            </InfoMessage>
            <InputGroup>
              <InputLabel valid={!errors.vat_percentage} htmlFor="edit_product_vat_percentage">
                {t('backend:vat_percentage_cost_price')}
              </InputLabel>
              <div className="grid grid-cols-2 gap-6">
                <InputWrapper>
                  <Input
                    {...register('vat_percentage')}
                    id="edit_product_vat_percentage"
                    className="pr-7"
                    onBlur={(event) => {
                      handleNumberBlur(event);
                      setValue('vat_percentage', event.target.value);
                    }}
                    aria-invalid={!!errors.vat_percentage}
                    inputMode="decimal"
                    max={100}
                  />
                  <label htmlFor="edit_product_vat_percentage">
                    %
                  </label>
                </InputWrapper>
              </div>
              <ErrorMessage error={errors.vat_percentage} attribute={t('backend:vat_percentage')} />
            </InputGroup>

            <InputGroup>
              <CheckboxLabel htmlFor="edit_product_required">
                <Checkbox
                  {...register('required')}
                  id="edit_product_required"
                  className="mt-1"
                />
                <span>
                  {t('backend:required_product')}
                </span>
              </CheckboxLabel>
              <CheckboxLabel htmlFor="edit_product_hide_on_invoice">
                <Checkbox
                  {...register('hide_on_invoice')}
                  id="edit_product_hide_on_invoice"
                  className="mt-1"
                />
                <span>
                  {t('backend:hide_product_from_invoice')}
                </span>
              </CheckboxLabel>
            </InputGroup>

            <InputGroup>
              <InputLabel valid={!errors.payment_methods}>
                {t('backend:allowed_payment_methods')}
              </InputLabel>
              <CheckboxLabel htmlFor="all_payment_methods">
                <Checkbox
                  onChange={() => setValue('payment_methods', [])}
                  disabled={selectedPaymentMethods.length === 0}
                  checked={selectedPaymentMethods.length === 0}
                  id="all_payment_methods"
                  className="mt-1"
                />
                <span>
                  {t('backend:all_payment_methods')}
                </span>
              </CheckboxLabel>
              {paymentMethods.map((paymentMethod) => (
                <CheckboxLabel htmlFor={`payment_method_${paymentMethod.id}`} key={paymentMethod.id}>
                  <Checkbox
                    {...register('payment_methods')}
                    value={paymentMethod.id}
                    id={`payment_method_${paymentMethod.id}`}
                    className="mt-1"
                  />
                  <span>
                    {t(`shared:payment_method.${paymentMethod.id}`)}
                  </span>
                </CheckboxLabel>
              ))}
            </InputGroup>
          </div>
        </div>
        <div className="grid grid-cols-[1fr_2fr] gap-8">
          <div>
            <h4 className="font-medium text-slate-500 mb-1">
              {t('backend:restrictions')}
            </h4>
            <div className="text-slate-500">
              {t('backend:product_restrictions_description')}
            </div>
          </div>
          <div className="space-y-6">
            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <InputGroup>
                <InputLabel required valid={!errors.start_date} htmlFor="product_start_date">
                  {t('backend:start_date')}
                </InputLabel>
                <Controller
                  control={control}
                  name="start_date"
                  rules={{ required: true, max: endDate || undefined }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DateInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ariaInvalid={!!errors.start_date}
                      endDate={endDate}
                      selectsStart
                      id="product_start_date"
                    />
                  )}
                />
                <ErrorMessage
                  error={errors.start_date}
                  attribute={t('backend:start_date')}
                  values={{ max: t('backend:end_date') }}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel valid={!errors.start_time} htmlFor="product_start_time">
                  {t('backend:start_time')}
                </InputLabel>
                <Controller
                  control={control}
                  name="start_time"
                  rules={{ max: endDate || undefined }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TimeInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ariaInvalid={!!errors.start_time}
                      id="product_start_time"
                      optional
                    />
                  )}
                />
                <ErrorMessage error={errors.start_time} attribute={t('backend:start_time')} />
              </InputGroup>
            </div>

            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <InputGroup>
                <InputLabel valid={!errors.end_date} htmlFor="product_end_date">
                  {t('backend:end_date')}
                </InputLabel>
                <Controller
                  control={control}
                  name="end_date"
                  rules={{ max: endDate || undefined }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <DateInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ariaInvalid={!!errors.end_date}
                      startDate={startDate}
                      selectsEnd
                      id="product_end_date"
                    />
                  )}
                />
                <ErrorMessage
                  error={errors.end_date}
                  attribute={t('backend:end_date')}
                  values={{ max: t('backend:start_date') }}
                />
              </InputGroup>
              <InputGroup>
                <InputLabel valid={!errors.end_time} htmlFor="product_end_time">
                  {t('backend:end_time')}
                </InputLabel>
                <Controller
                  control={control}
                  name="end_time"
                  rules={{ max: endDate || undefined }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <TimeInput
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      ariaInvalid={!!errors.end_time}
                      id="product_end_time"
                      optional
                    />
                  )}
                />
                <ErrorMessage error={errors.end_time} attribute={t('backend:end_time')} />
              </InputGroup>
            </div>

            <div className="grid grid-cols-[1fr_1fr] gap-6">
              <InputGroup>
                <InputLabel valid={!errors.capacity} htmlFor="edit_product_capacity">
                  {t('backend:capacity')}
                </InputLabel>
                <Input
                  {...register('capacity')}
                  onBlur={(event) => {
                    handleIntegerBlur(event);
                    setValue('capacity', event.target.value);
                  }}
                  id="edit_product_capacity"
                  placeholder="∞"
                  aria-invalid={!!errors.capacity}
                  inputMode="decimal"
                  min={0}
                  max={999999}
                />
                <ErrorMessage error={errors.capacity} attribute={t('backend:capacity')} />
              </InputGroup>
            </div>

            {!product.is_campaign_product && (
              <>
                <InputGroup>
                  <InputLabel htmlFor="edit_product_projects">
                    {t('backend:projects')}
                  </InputLabel>
                  <div className="space-y-2">
                    {fields.map((field, index) => (field.value || (selectedProjectIds.length < projects.length)) && (
                      <Controller
                        control={control}
                        name={`projects.${index}.value`}
                        key={field.id}
                        render={({ field: { onChange, onBlur, value } }) => (
                          <Picker
                            selected={value}
                            items={projects.filter((project) => (
                              project.id === projectsValue[index].value || !selectedProjectIds.includes(project.id)
                            ))}
                            onChange={(value) => {
                              if (value) {
                                onChange(value.id);
                                handleProjectChange();
                              } else {
                                remove(index);
                              }
                            }}
                            onBlur={onBlur}
                            icon={<Folder />}
                            optional
                            placeholder={fields.length === 1 ? t('backend:all_projects') : undefined}
                            id={index === fields.length - 1 ? 'edit_product_projects' : undefined}
                          />
                        )}
                      />
                    ))}
                  </div>
                </InputGroup>

                <div className="space-y-6">
                  <InputGroup>
                    <InputLabel htmlFor="edit_product_dont_require_opt_in">
                      {t('backend:show_product_on')}
                    </InputLabel>
                    <RadioLabel htmlFor="edit_product_dont_require_opt_in">
                      <Radio
                        {...register('require_opt_in')}
                        value="no"
                        id="edit_product_dont_require_opt_in"
                        className="mt-1"
                      />
                      <span>
                        {t('backend:all_campaigns')}
                      </span>
                    </RadioLabel>
                    <RadioLabel htmlFor="edit_product_require_opt_in">
                      <Radio
                        {...register('require_opt_in')}
                        value="yes"
                        id="edit_product_require_opt_in"
                        className="mt-1"
                      />
                      <span>
                        {t('backend:only_campaigns_with_opt_in')}
                      </span>
                    </RadioLabel>
                  </InputGroup>

                  <FadeIn
                    when={requireOptIn}
                    render={() => (
                      <div className="space-y-6">
                        <InfoMessage>
                          {t('backend:product_require_opt_in_description')}
                        </InfoMessage>
                        <InputGroup>
                          <InputLabel htmlFor="edit_product_campaigner_description">
                            {t('backend:product_campaigner_description')}
                          </InputLabel>
                          <InputDescription>
                            {t('backend:product_campaigner_description_description')}
                          </InputDescription>
                          <Controller
                            control={control}
                            name="campaigner_description"
                            render={({ field: { onChange, onBlur, value } }) => (
                              <Editor
                                value={value}
                                onChange={onChange}
                                onBlur={onBlur}
                              />
                            )}
                          />
                          <ErrorMessage
                            error={errors.campaigner_description}
                            attribute={t('backend:campaigner_description')}
                          />
                        </InputGroup>
                      </div>
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </div>
        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}

EditProductForm.layout = (page: ReactNode) => <Layout>{page}</Layout>;
